import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FabricService } from '../../services/fabric.service';
import { FabricNotebookJsonService } from '../../services/fabric-notebook-json.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FabricItemBaseComponent } from '../shared/fabric-item-base.directive';
import { FabricItem } from '../../models/FabricItem';
import { NotebookJsonMapping } from '../../models/NotebookJsonMapping';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-notebooks',
  standalone: true,
  imports: [CommonModule, FormsModule, MatProgressSpinnerModule, MatIconModule],
  templateUrl: './notebooks.component.html',
  styleUrl: '../shared/fabric-item-base.directive.scss',
})
export class NotebooksComponent extends FabricItemBaseComponent {
  protected fabricNotebookJsonService: FabricNotebookJsonService;
  combinations: NotebookJsonMapping[] = [];
  system: string = '';
  company: string = '';
  sourceTable: string = '';
  targetTable: string = '';
  protected areCombinationsLoading: boolean = false;

  protected get itemType(): string {
    return 'notebook';
  }

  protected get jobType(): string {
    return 'RunNotebook';
  }

  get notebooks(): FabricItem[] {
    return this.items;
  }

  get selectedNotebook(): FabricItem | null {
    return this.selectedItem;
  }

  set selectedNotebook(value: FabricItem | null) {
    this.selectedItem = value;
  }

  get filteredNotebooks() {
    return this.filteredItems;
  }

  selectNotebook(notebook: FabricItem) {
    this.areCombinationsLoading = true;
    this.selectItem(notebook, () => {
      if (this.selectedItem) {
        this.getNotebookCombinations(this.selectedItem.name);
        this.system = '';
        this.company = '';
        this.sourceTable = '';
        this.targetTable = '';

        this.parameterFields.forEach(p => {
          if (p.label === 'system') {
            this.system = p.value;
          } else if (p.label === 'company') {
            p.value = '';
          } else if (p.label === 'source_table' || p.label === 'endpoint') {
            p.value = '';
          } else if (p.label === 'target_table') {
            p.value = '';
          }
        });
      }
    });
  }

  runNotebook(id: string) {
    this.runItem(id);
  }

  saveNotebook() {
    this.saveItem();
  }

  editNotebook() {
    this.editItem();
  }

  constructor(fabricService: FabricService, fabricNotebookJsonService: FabricNotebookJsonService) {
    super(fabricService);
    this.fabricNotebookJsonService = fabricNotebookJsonService;
  }

  protected override extractParameterFields(params: any) {
    Object.entries(params).forEach(([key, value]) => {
      this.parameterFields.push({
        label: key,
        value: value
      });
    });
  }

  getNotebookCombinations(notebookName: string) {
    return this.fabricNotebookJsonService.getNotebookCombinations(notebookName).subscribe({
      next: (combinations) => {
        this.combinations = combinations;
        this.areCombinationsLoading = false;
      },
      error: (error) => {
        console.error('Error fetching notebook combinations:', error);
      }
    });
  }

  setParameter(label: string, value: string) {
    switch (label) {
      case 'system':
        this.system = value;
        this.company = '';
        this.sourceTable = '';
        this.targetTable = '';

        this.parameterFields.forEach(p => {
          if (p.label === 'company') {
            p.value = '';
          }
          if (p.label === 'source_table' || p.label === 'endpoint') {
            p.value = '';
          }
          if (p.label === 'target_table') {
            p.value = '';
          }
        });
        break;
      case 'company':
        this.company = value;
        this.sourceTable = '';
        this.targetTable = '';

        this.parameterFields.forEach(p => {
          if (p.label === 'source_table' || p.label === 'endpoint') {
            p.value = '';
          }
          if (p.label === 'target_table') {
            p.value = '';
          }
        });
        break;
      case 'source_table':
      case 'endpoint':
        this.sourceTable = value;
        this.targetTable = '';

        this.parameterFields.forEach(p => {
          if (p.label === 'target_table') {
            p.value = '';
          }
        });
        break;
      case 'target_table':
        this.targetTable = value;
        break;
    }

    const param = this.parameterFields.find(p => p.label === label);
    if (param) {
      param.value = value;
    }
  }

  getSystemOptions(): string[] {
    return Array.from(new Set(this.combinations.map(c => c.systemName)));
  }

  getCompanyOptions(): string[] {
    if (this.system !== '') {
      return Array.from(new Set(this.combinations
        .filter(c => c.systemName === this.system)
        .map(c => c.company)));
    }
    return [];
  }

  getSourceTableOptions(): string[] {
    if (this.company !== '' && this.system !== '') {
      return Array.from(new Set(this.combinations
        .filter(c => c.systemName === this.system && c.company === this.company)
        .map(c => c.sourceTable)));
    }
    return [];
  }

  getTargetTableOptions(): string[] {
    if (this.sourceTable !== '' && this.company !== '' && this.system !== '') {
      return Array.from(new Set(this.combinations
        .filter(c => c.systemName === this.system && c.company === this.company && c.sourceTable === this.sourceTable)
        .map(c => c.targetTable)));
    }
    return [];
  }
}
